export interface AccountCodeNode {
  glAccountId: string;
  code: string;
  parentAccountId: string | null;
}

interface NumericSegment {
  sequence: number;
  width: number;
}

const NUMERIC_ACCOUNT_CODE_SEGMENT_RE = /^\d+$/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getLastAccountCodeSegment(code: string): string {
  const lastDotIndex = code.lastIndexOf('.');
  return lastDotIndex === -1 ? code : code.slice(lastDotIndex + 1);
}

function parseNumericSegment(segment: string): NumericSegment | null {
  if (!NUMERIC_ACCOUNT_CODE_SEGMENT_RE.test(segment)) {
    return null;
  }

  const sequence = Number.parseInt(segment, 10);
  if (!Number.isFinite(sequence) || sequence <= 0) {
    return null;
  }

  return { sequence, width: segment.length };
}

function maxNumericSegmentWidth(segments: readonly NumericSegment[]): number {
  return segments.length ? Math.max(...segments.map((segment) => segment.width)) : 1;
}

/** Formats a supplied account code's final numeric segment like its siblings. */
export function formatAccountCodeToSiblingWidth(
  code: string,
  siblingCodes: readonly string[],
): string {
  const lastDotIndex = code.lastIndexOf('.');
  const prefix = lastDotIndex === -1 ? '' : code.slice(0, lastDotIndex + 1);
  const segment = lastDotIndex === -1 ? code : code.slice(lastDotIndex + 1);
  const requested = parseNumericSegment(segment);
  if (!requested) return code;

  const siblingSegments = siblingCodes
    .map(getLastAccountCodeSegment)
    .map(parseNumericSegment)
    .filter((sibling): sibling is NumericSegment => sibling !== null);
  if (siblingSegments.length === 0) return code;

  return `${prefix}${String(requested.sequence).padStart(
    maxNumericSegmentWidth(siblingSegments),
    '0',
  )}`;
}

/**
 * The separator a chart puts between a parent code and its child's sequence.
 * Brazilian charts write `1.1.02.000001`; Mexican ones `205-01-001`. Inferred
 * from the parent's own code: a dash-separated parent gets dash-separated
 * children, anything else gets dots. Never mixed within one chart.
 */
export function childAccountCodeSeparator(parentCode: string): '.' | '-' {
  return parentCode.includes('-') && !parentCode.includes('.') ? '-' : '.';
}

function childSequenceRe(parentCode: string): RegExp {
  return new RegExp(`^${escapeRegExp(parentCode)}[.\\-](\\d+)$`);
}

export function extractChildAccountCodeSequence(code: string, parentCode: string): number | null {
  const match = childSequenceRe(parentCode).exec(code);
  const segment = match?.[1];
  if (!segment) {
    return null;
  }

  return parseNumericSegment(segment)?.sequence ?? null;
}

export function isDirectNumericChildAccountCode(code: string, parentCode: string): boolean {
  return extractChildAccountCodeSequence(code, parentCode) !== null;
}

export function buildChildAccountCode(parentCode: string, sequence: number, width: number): string {
  return `${parentCode}${childAccountCodeSeparator(parentCode)}${String(sequence).padStart(width, '0')}`;
}

export function computeNextChildAccountCode(
  parentCode: string,
  childCodes: readonly string[],
): string {
  const numericSegments = childCodes
    .map((code) => {
      const segment = childSequenceRe(parentCode).exec(code)?.[1];
      return segment ? parseNumericSegment(segment) : null;
    })
    .filter((segment): segment is NumericSegment => segment !== null);

  const maxSequence = numericSegments.length
    ? Math.max(...numericSegments.map((segment) => segment.sequence))
    : 0;
  const width = maxNumericSegmentWidth(numericSegments);

  return buildChildAccountCode(parentCode, maxSequence + 1, width);
}

export function computeNextAccountCode(
  accounts: readonly AccountCodeNode[],
  parentAccountId: string | null,
): string {
  const siblings = accounts.filter((account) => account.parentAccountId === parentAccountId);
  const parent = parentAccountId
    ? (accounts.find((account) => account.glAccountId === parentAccountId) ?? null)
    : null;

  const numericSegments = siblings
    .map((account) => parseNumericSegment(getLastAccountCodeSegment(account.code)))
    .filter((segment): segment is NumericSegment => segment !== null);

  const maxSequence = numericSegments.length
    ? Math.max(...numericSegments.map((segment) => segment.sequence))
    : 0;
  const width = maxNumericSegmentWidth(numericSegments);

  const nextSegment = String(maxSequence + 1).padStart(width, '0');
  return parent ? `${parent.code}.${nextSegment}` : nextSegment;
}
